import React, { useState, useEffect } from 'react';
import { ScheduleCalendar } from '../components/maintenance/ScheduleCalendar';
import { ScheduleBuilder } from '../components/maintenance/ScheduleBuilder';
import { api } from '../utils/api';
import {
  MaintenanceEmployee,
  Machine,
  ScheduleOverview,
  CreateScheduleInput,
  UpdateScheduleInput,
} from '../types/maintenance';

export const ScheduleManagementPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [employees, setEmployees] = useState<MaintenanceEmployee[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string[]>>({});
  const [scheduledDates, setScheduledDates] = useState<string[]>([]);
  const [currentScheduleId, setCurrentScheduleId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneTargetDate, setCloneTargetDate] = useState<string>('');

  useEffect(() => {
    loadEmployees();
    loadMachines();
    loadScheduledDates();
  }, []);

  useEffect(() => {
    loadScheduleForDate(selectedDate);
  }, [selectedDate]);

  const loadEmployees = async () => {
    try {
      const response = await api.get<MaintenanceEmployee[]>('/maintenance-employees');
      setEmployees(response.data);
    } catch (err: any) {
      console.error('Error loading employees:', err);
      setError('Erro ao carregar funcionários');
    }
  };

  const loadMachines = async () => {
    try {
      const response = await api.get<{ machines: Machine[] }>('/machines');
      console.log('Machines loaded:', response.data);
      console.log('Number of machines:', response.data.machines?.length);
      setMachines(response.data.machines || []);
    } catch (err: any) {
      console.error('Error loading machines:', err);
      setError('Erro ao carregar máquinas');
    }
  };

  const loadScheduledDates = async () => {
    try {
      // Load schedules for current month and adjacent months
      const startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
      const endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 2, 0);
      
      const response = await api.get<{ dates: string[] }>(
        `/maintenance-schedules/dates?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
      );
      setScheduledDates(response.data.dates || []);
    } catch (err: any) {
      console.error('Error loading scheduled dates:', err);
    }
  };

  const loadScheduleForDate = async (date: Date) => {
    setIsLoading(true);
    setError(null);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.get<ScheduleOverview>(`/maintenance-schedules/${dateStr}`);
      
      if (response.data) {
        const schedule = response.data;
        const newAssignments: Record<string, string[]> = {};
        
        schedule.employeeSchedules.forEach((empSchedule) => {
          newAssignments[empSchedule.employee.id] = empSchedule.assignedMachines.map(
            (am) => am.machine.id
          );
        });
        
        setAssignments(newAssignments);
        setCurrentScheduleId(schedule.scheduleId || null); // Use the actual schedule ID
      } else {
        // No schedule for this date
        setAssignments({});
        setCurrentScheduleId(null);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No schedule exists for this date
        setAssignments({});
        setCurrentScheduleId(null);
      } else {
        console.error('Error loading schedule:', err);
        setError('Erro ao carregar agenda');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      // Validate that all machines exist
      const allMachineIds = Object.values(assignments).flat();
      const invalidMachines = allMachineIds.filter(
        (id) => !machines.find((m) => m.id === id)
      );
      
      if (invalidMachines.length > 0) {
        setError('Algumas máquinas atribuídas não existem mais no sistema');
        return;
      }

      // Convert assignments to API format
      const assignmentsArray = Object.entries(assignments).map(
        ([employeeId, machineIds]) => ({
          employeeId,
          machineIds,
        })
      );

      if (currentScheduleId) {
        // Update existing schedule
        const updateData: UpdateScheduleInput = {
          assignments: assignmentsArray,
        };
        await api.put(`/maintenance-schedules/${currentScheduleId}`, updateData);
        setSuccessMessage('Agenda atualizada com sucesso!');
      } else {
        // Create new schedule
        const createData: CreateScheduleInput = {
          date: dateStr,
          assignments: assignmentsArray,
        };
        await api.post('/maintenance-schedules', createData);
        setCurrentScheduleId(dateStr);
        setSuccessMessage('Agenda criada com sucesso!');
      }

      // Reload scheduled dates
      await loadScheduledDates();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving schedule:', err);
      setError(err.response?.data?.message || 'Erro ao salvar agenda');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloneSchedule = async () => {
    if (!cloneTargetDate) {
      setError('Selecione uma data de destino');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const sourceDateStr = selectedDate.toISOString().split('T')[0];
      
      await api.post('/maintenance-schedules/clone', {
        sourceDate: sourceDateStr,
        targetDate: cloneTargetDate,
      });

      setSuccessMessage('Agenda clonada com sucesso!');
      setShowCloneModal(false);
      setCloneTargetDate('');
      
      // Reload scheduled dates
      await loadScheduledDates();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error cloning schedule:', err);
      setError(err.response?.data?.message || 'Erro ao clonar agenda');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const hasUnsavedChanges = () => {
    // Simple check - in production, you'd want more sophisticated change tracking
    return Object.keys(assignments).length > 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gerenciamento de Agendas
          </h1>
          <p className="text-gray-600">
            Crie e gerencie agendas de manutenção para funcionários
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <svg
              className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
            <svg
              className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-green-800">Sucesso</h3>
              <p className="text-sm text-green-700 mt-1">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Section */}
          <div className="lg:col-span-1">
            <ScheduleCalendar
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              scheduledDates={scheduledDates}
            />

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <button
                onClick={handleSaveSchedule}
                disabled={isSaving || !hasUnsavedChanges()}
                className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center"
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Salvando...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                      />
                    </svg>
                    {currentScheduleId ? 'Atualizar Agenda' : 'Salvar Agenda'}
                  </>
                )}
              </button>

              <button
                onClick={() => setShowCloneModal(true)}
                disabled={!currentScheduleId || isSaving}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Clonar Agenda
              </button>
            </div>

            {/* Selected Date Info */}
            <div className="mt-6 bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Data Selecionada
              </h3>
              <p className="text-sm text-gray-900 capitalize">
                {formatDate(selectedDate)}
              </p>
              {currentScheduleId && (
                <p className="text-xs text-green-600 mt-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Agenda existente
                </p>
              )}
            </div>
          </div>

          {/* Schedule Builder Section */}
          <div className="lg:col-span-2">
            {isLoading ? (
              <div className="flex justify-center items-center h-96 bg-white rounded-lg shadow">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Carregando agenda...</p>
                </div>
              </div>
            ) : (
              <ScheduleBuilder
                employees={employees}
                availableMachines={machines}
                assignments={assignments}
                onAssignmentChange={setAssignments}
              />
            )}
          </div>
        </div>
      </div>

      {/* Clone Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Clonar Agenda
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Copiar a agenda de {formatDate(selectedDate)} para outra data.
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Destino
              </label>
              <input
                type="date"
                value={cloneTargetDate}
                onChange={(e) => setCloneTargetDate(e.target.value)}
                className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCloneModal(false);
                  setCloneTargetDate('');
                  setError(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCloneSchedule}
                disabled={!cloneTargetDate || isSaving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? 'Clonando...' : 'Clonar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
